import React from "react";
import {
  BookOpen,
  Users,
  CheckCircle,
  AlertTriangle,
  Menu,
} from "lucide-react";

// Helper component for the customized Progress Bars
const CustomProgressBar = ({
  value,
  max,
  colorClass,
  bgClass = "bg-orange-100",
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={`h-3 w-full rounded-full ${bgClass}`}>
      <div
        className={`h-full rounded-full ${colorClass}`}
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  );
};

// Helper for Semi-Circle Gauge
// Helper for Semi-Circle Gauge
const Gauge = ({
  value,
  max = 100,
  label,
  subLabel,
  color,
  trackColor = "#FFEDD5",
}) => {
  const radius = 35; // Slightly smaller to ensure no overlap
  const stroke = 10;
  const cx = 50;
  const cy = 40;

  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius
    } ${cy}`;

  const circumference = radius * Math.PI;
  const strokeDashoffset = circumference - (value / max) * circumference;

  return (
    <div className="flex flex-col items-center relative w-full group">
      <div className="relative flex flex-col items-center justify-center h-[60px] w-full max-w-[90px]">
        {/* Menu Icon container - Styled better */}
        <div className="absolute -top-3 right-0 opacity-40 group-hover:opacity-100 transition-opacity">
          <Menu size={14} strokeWidth={2.5} className="text-gray-400" />
        </div>

        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 55"
          className="overflow-visible"
        >
          <path
            d={arcPath}
            stroke={trackColor}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
          />
          <path
            d={arcPath}
            stroke={color}
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        </svg>

        <div className="absolute bottom-2 text-center">
          <span
            className="text-sm sm:text-base font-black tracking-tight"
            style={{ color: color }}
          >
            {value}
            {subLabel}
          </span>
        </div>
      </div>
      <span className="text-[9px] sm:text-[11px] text-gray-400 font-bold uppercase tracking-wider mt-2 text-center leading-tight max-w-[80px]">
        {label}
      </span>
    </div>
  );
};

export default function TrainingProgress({
  progress,
  stats,
  loading,
  restaurants,
}) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-full shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-32 bg-gray-200 rounded w-full"></div>
        </div>
      </div>
    );
  }

  if (!progress) return null;

  const {
    food,
    wine,
    overAllProgress,
    allLessons,
    completedLessons,
    restaurantsSummary,
  } = progress;
  const userRole = localStorage.getItem("userRole");

  // Derive data from props or defaults
  // "stats" prop passed from Dashboard contains totalEmployees, missingTraining, etc.
  const activeLearners = stats?.totalEmployees || 0;
  // stats.missedTraining might be a count or we might need to derive it.
  // Based on StatCard logic: (stats.totalEmployees * (stats.missedTrainingRate/100))
  // Or if stats.missedTraining is available directly. Let's assume passed stats object has what we need
  const overdueEmployees = stats?.missedTraining || 0; // Using raw value if available, or 0

  // Hardcoded for visual match if data missing
  // The image shows "Restaurants >=90% Completion" etc.
  // We will map "Food Lessons" and "Wine Lessons" to these slots to show available data in the requested style
  // but using the labels from the image would be misleading if we show Food/Wine data.
  // User asked "jesy image ma define ha wesa kerdo" (make it as defined in image).
  // I will use the visual style but keep the LABELS relevant to the data for correctness,
  // OR I can use the labels "Food Category" and "Wine Category" which fits "Category Progress".

  // Logic for Category Progress Bars (Calculated locally for accuracy)
  const totalRestaurants = restaurants?.length || 0;
  const countAbove90 =
    restaurants?.filter(
      (r) => (r.dashboard?.lessonCompletionPercentage || 0) >= 90
    ).length || 0;
  const countBelow50 =
    restaurants?.filter(
      (r) => (r.dashboard?.lessonCompletionPercentage || 0) <= 50
    ).length || 0;

  // Visibility Rules
  // Director with 2+ restaurants: Show Bars + Pill
  // Director with 1 restaurant: Hide Section
  // Manager: Hide Bars, Show Pill (Most Improved Employee)
  const showBars = userRole === "director" && totalRestaurants >= 2;
  const showPill =
    (userRole === "director" && totalRestaurants >= 2) || userRole === "manager";
  const showCategoryProgressSection = showBars || showPill;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-full font-sans">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-4">
        <div className="p-1.5 bg-red-50 rounded text-[#881337]">
          <BookOpen size={18} strokeWidth={2.5} />
        </div>
        <h3 className="text-[#881337] font-bold text-base sm:text-lg">
          Training Progress{" "}
          {restaurants?.length === 1 && userRole === "manager"
            ? `(${restaurants[0].name})`
            : `(All Restaurants)`}
        </h3>
      </div>

      {/* Knowledge Overview */}
      <div className="mb-8">
        <h4 className="text-gray-700 font-semibold mb-4 text-m text-left">
          Knowledge Overview
        </h4>

        <div className="space-y-4">
          {/* Food Knowledge */}
          <div>
            <div className="flex justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-600">
                Food Knowledge (avg.)
              </span>
              <span className="text-xs font-bold text-[#881337]">
                {food.foodAverageScoreInPercentage}%
              </span>
            </div>
            <CustomProgressBar
              value={food.foodAverageScoreInPercentage}
              max={100}
              colorClass="bg-[#881337]"
              bgClass="bg-[#FEE2E2]" // Light red
            />
          </div>

          {/* Wine Knowledge */}
          <div>
            <div className="flex justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-600">
                Wine Knowledge (avg.)
              </span>
              <span className="text-xs font-bold text-[#F472B6]">
                {wine.wineAverageScoreInPercentage}%
              </span>
            </div>
            <CustomProgressBar
              value={wine.wineAverageScoreInPercentage}
              max={100}
              colorClass="bg-[#F472B6]"
              bgClass="bg-[#FFF1F2]" // Very light pink
            />
          </div>
        </div>
      </div>

      {/* Training Health */}
      <div className="mb-8">
        <h4 className="text-gray-700 font-semibold mb-4 text-m text-left">
          Training Health
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Active Learners */}
          <div className="bg-[#FFFBEB] rounded-lg p-4 flex flex-col items-center justify-center text-center">
            <img
              src="/activelerner.png"
              alt="Active Learners"
              className="w-8 h-8 object-contain mb-2"
            />
            <span className="text-2xl font-bold text-gray-800">
              {activeLearners}
            </span>
            <span className="text-[10px] text-gray-500 font-medium">
              Active Learners
            </span>
          </div>

          {/* Completed Lessons */}
          <div className="bg-[#FFFBEB] rounded-lg p-4 flex flex-col items-center justify-center text-center">
            <img
              src="/completedlessons.png"
              alt="Completed Lessons"
              className="w-8 h-8 object-contain mb-2"
            />
            <span className="text-2xl font-bold text-gray-800">
              {completedLessons}
            </span>
            <span className="text-[10px] text-gray-500 font-medium">
              Completed Lessons
            </span>
          </div>

          {/* Overdue Employees */}
          <div className="bg-[#FFFBEB] rounded-lg p-4 flex flex-col items-center justify-center text-center">
            <img
              src="/overdueemploye.png"
              alt="Overdue Employees"
              className="w-8 h-8 object-contain mb-2"
            />
            <span className="text-2xl font-bold text-gray-800">
              {overdueEmployees}
            </span>
            <span className="text-[10px] text-gray-500 font-medium">
              Overdue Employees
            </span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="my-8">
        <h4 className="text-gray-700 font-bold mb-6 text-base text-left">
          Performance Metrics
        </h4>
        <div className="grid grid-cols-3 gap-1 sm:gap-4 px-1">
          {/* Avg Completion */}
          <div className="flex flex-col items-center scale-90 sm:scale-100 origin-top">
            <Gauge
              value={parseFloat(overAllProgress) || 0}
              max={100}
              color="#881337"
              trackColor="#FDE1BC"
              label="Avg. Completion"
              subLabel="%"
            />
          </div>

          {/* Avg Score */}
          <div className="flex flex-col items-center scale-90 sm:scale-100 origin-top">
            <Gauge
              value={parseFloat(progress.averageScore) || 0}
              max={100}
              color="#BE185D"
              trackColor="#FCE7F3" // Light Pink
              label="Avg. Score"
              subLabel="%"
            />
          </div>

          {/* Avg Attempts */}
          <div className="flex flex-col items-center scale-90 sm:scale-100 origin-top">
            <Gauge
              value={parseFloat(progress.averageAttempts) || 0}
              max={10}
              color="#C2410C"
              trackColor="#FFEDD5" // Light Orange
              label="Avg. Attempts"
              subLabel=""
            />
          </div>
        </div>
      </div>

      {/* Category Progress Section */}
      {showCategoryProgressSection && (
        <div className="mb-0">
          <h4 className="text-gray-700 text-left font-semibold mb-4 text-l">
            Category Progress
          </h4>
          <div className="space-y-6">
            {showBars && (
              <>
                {/* Bar #1: Restaurants >= 90% */}
                <div>
                  <div className="flex justify-between mb-2 items-end">
                    <span className="text-xs font-medium text-gray-600">
                      Restaurants ≥90% Completion
                    </span>
                    <span className="text-xs font-bold text-[#D97706]">
                      {countAbove90} / {totalRestaurants}
                    </span>
                  </div>
                  <CustomProgressBar
                    value={
                      totalRestaurants > 0
                        ? (countAbove90 / totalRestaurants) * 100
                        : 0
                    }
                    max={100}
                    colorClass="bg-[#D97706]"
                    bgClass="bg-[#FEF3C7]"
                  />
                </div>

                {/* Bar #2: Restaurants <= 50% */}
                <div>
                  <div className="flex justify-between mb-2 items-end">
                    <span className="text-xs font-medium text-gray-600">
                      Restaurants ≤50% Completion
                    </span>
                    <span className="text-xs font-bold text-[#D97706]">
                      {countBelow50} / {totalRestaurants}
                    </span>
                  </div>
                  <CustomProgressBar
                    value={
                      totalRestaurants > 0
                        ? (countBelow50 / totalRestaurants) * 100
                        : 0
                    }
                    max={100}
                    colorClass="bg-[#D97706]"
                    bgClass="bg-[#FEF3C7]"
                  />
                </div>
              </>
            )}

            {/* Highlight Pill (Most Improved) */}
            {showPill && (
              <div className="bg-[#FEF08A] rounded-lg p-3 flex justify-between items-center text-sm">
                <span className="font-semibold text-[#854D0E] text-s">
                  {userRole === "manager"
                    ? "Most Improved Employee"
                    : "Most Improved Restaurant"}
                </span>
                <span className="font-bold text-[#854D0E]">
                  {userRole === "manager"
                    ? restaurantsSummary?.mostImprovedEmployee
                      ? `${restaurantsSummary.mostImprovedEmployee.employeeName} (+${restaurantsSummary.mostImprovedEmployee.improvement.toFixed(0)}%)`
                      : "—"
                    : restaurantsSummary?.mostImprovedRestaurant?.restaurantName ||
                    "—"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
